import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import buttonPropTypes, { textAndA11yText } from './propTypes';
import ButtonBase from './ButtonBase';
import { useThemeTokensCallback } from '../ThemeProvider';
import { a11yProps, getTokensPropType, focusHandlerProps, resolvePressableState, selectTokens, useInputValue } from '../utils';
import Icon from '../Icon';
import { getStackedContent } from '../StackView';
import { getPressHandlersWithArgs } from '../utils/pressability';
import { jsx as _jsx } from "react/jsx-runtime";
const selectIconTokens = _ref => {
  let {
    icon,
    iconPosition,
    iconSpace,
    iconSize,
    iconColor,
    iconBackground,
    iconBorderRadius,
    iconAlignSelf,
    iconPadding,
    iconTranslateX,
    iconTranslateY
  } = _ref;
  return {
    icon,
    iconPosition,
    iconSpace,
    iconWrapperStyle: {
      backgroundColor: iconBackground,
      borderRadius: iconBorderRadius,
      alignSelf: iconAlignSelf,
      padding: iconPadding,
      ...Platform.select({
        // TODO: https://github.com/telus/universal-design-system/issues/487
        web: {
          transition: 'color 200ms, background 200ms'
        }
      })
    },
    iconTokens: {
      size: iconSize,
      color: iconColor,
      translateX: iconTranslateX,
      translateY: iconTranslateY
    }
  };
};
const ButtonDropdown = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    value,
    initialValue,
    onChange,
    label,
    tokens,
    variant,
    inactive = false,
    readOnly = false,
    children = null,
    accessibilityRole = 'radio',
    ...props
  } = _ref2;
  const {
    currentValue: isOpen,
    setValue: setIsOpen
  } = useInputValue({
    value,
    initialValue,
    onChange,
    readOnly
  }, 'useButtonDropdownValues');
  const extraState = {
    open: isOpen,
    inactive,
    ...variant
  };
  const getTokens = useThemeTokensCallback('ButtonDropdown', tokens, extraState);
  const getButtonTokens = buttonState => selectTokens('Button', getTokens(buttonState));

  // Pass an object of relevant component state as first argument for any passed-in press handlers
  const pressHandlers = getPressHandlersWithArgs(props, [{
    label,
    open: isOpen
  }]);
  const handlePress = event => {
    if (!inactive) {
      if (pressHandlers.onPress) pressHandlers?.onPress(event);
      setIsOpen(!isOpen, event);
    }
  };
  return /*#__PURE__*/_jsx(ButtonBase, {
    ref: ref,
    ...pressHandlers,
    onPress: handlePress,
    tokens: getButtonTokens,
    inactive: inactive,
    icon: () => null,
    accessibilityRole: accessibilityRole,
    ...props,
    children: _ref3 => {
      let {
        textStyles,
        ...buttonState
      } = _ref3;
      // TODO: once Icon/IconButton designs are stable, see if this sort of styling around
      // an icon should go in Icon itself, or possibly via an IconText token set. Related issues:
      // - Icon: https://github.com/telus/universal-design-system/issues/327
      // - IconButton: https://github.com/telus/universal-design-system/issues/281
      // - Token sets: https://github.com/telus/universal-design-system/issues/782

      const itemTokens = getTokens(buttonState);
      const {
        iconTokens,
        iconPosition,
        iconSpace,
        iconWrapperStyle,
        icon: IconComponent
      } = selectIconTokens(itemTokens);
      const iconContent = IconComponent ? /*#__PURE__*/_jsx(View, {
        style: iconWrapperStyle,
        children: /*#__PURE__*/_jsx(Icon, {
          icon: IconComponent,
          tokens: iconTokens
        })
      }) : null;
      const childrenContent = () => typeof children === 'function' ? children({
        ...resolvePressableState(buttonState, extraState),
        textStyles
      }) : children;
      const content = children ? childrenContent() : /*#__PURE__*/_jsx(Text, {
        style: textStyles,
        children: label
      });
      return getStackedContent(iconPosition === 'left' ? [iconContent, content] : [content, iconContent], {
        space: iconSpace,
        direction: 'row'
      });
    }
  });
});
ButtonDropdown.displayName = 'ButtonDropdown';
ButtonDropdown.propTypes = {
  ...a11yProps.types,
  ...focusHandlerProps.types,
  ...buttonPropTypes,
  children: textAndA11yText,
  tokens: getTokensPropType('ButtonDropdown'),
  /**
   * Callback called when a controlled ButtonDropdown gets interacted with.
   */
  onChange: PropTypes.func,
  /**
   * `value` prop is being used to set the 'open' state of ButtonDropdown. Use it for
   *  controlled ButtonDropdown. For uncontrolled ButtonDropdown, use `initialValue`.
   */
  value: PropTypes.bool,
  /**
   * Use `initialValue` to provide the initial value for an uncontrolled version.
   */
  initialValue: PropTypes.bool,
  /**
   * The label of ButtonDropdown.
   */
  label: PropTypes.string,
  /**
   * By default, `ButtonDropdown` is treated by accessibility tools as a radio button.
   */
  accessibilityRole: PropTypes.string
};
export default ButtonDropdown;